import { Box, Button, Card, styled, Typography, Divider, CircularProgress, IconButton, Chip } from "@mui/material";
import Grid from "@mui/material/Grid2";

export const StyledPaymentCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.borderRadius.radius.regular || 8,
  border: "1px solid #e8eaed",
  marginBottom: "16px",
  overflow: "hidden"
}));

export const StyledAdminHeader = styled(Box)(() => ({
  backgroundColor: "#fafafa",
  padding: "12px 24px",
  borderBottom: "1px solid #e8eaed",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
}));

export const StyledPayNowButton = styled(Button)(() => ({
  backgroundColor: "#FF8500",
  color: "#fff",
  fontWeight: 700,
  textTransform: "none",
  padding: "6px 32px",
  borderRadius: "40px",
  "&:hover": {
    backgroundColor: "#E07400"
  },
  "&:disabled": {
    backgroundColor: "#e0e0e0",
    color: "#a6a6a6"
  }
}));

export const StyledRetryButton = styled(Button)(() => ({
  borderColor: "#FF8500",
  color: "#FF8500",
  fontWeight: 700,
  textTransform: "none",
  borderRadius: "5px",
  "&:hover": {
    borderColor: "#E07400",
    backgroundColor: "#fff8f2"
  }
}));

export const StyledFlexBetween = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
}));

export const StyledSummaryHeaderRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "8px",
  marginBottom: "4px",
  flexWrap: "wrap",
}));

export const StyledInlineActionsRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
}));

export const StyledBillAddedChip = styled(Chip)(() => ({
  height: 24,
  fontWeight: 600,
}));

export const StyledColumnBox = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  paddingTop: "8px",
  paddingBottom: "8px"
}));

export const StyledColumnBoxGap = styled(StyledColumnBox)(() => ({
  gap: "24px"
}));

export const StyledRowBox = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "16px"
}));

// Admin & Timeline Specific Styled Components
export const PAYMENT_DETAIL_COLORS = {
  sectionTitle: "#0B65B5",
  fieldLabel: "#000000",
  fieldValue: "#04182C",
  accentBorder: "#0B65B5",
  timelineText: "#03336E",
  timelineTime: "#475467",
};

export const StyledAttemptCard = styled(Box)(({ theme }) => ({
  padding: "20px 22px",
  borderRadius: theme.shape?.custom?.regularRadius ?? "8px",
  border: theme.borders?.highlighted ?? "1px solid #CAE6FF",
  backgroundColor: theme.palette?.custom?.white ?? "#FFFFFF",
}));

export const StyledAttemptHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "18px",
  paddingBottom: "14px",
  borderBottom: `1px solid ${theme.palette?.custom?.tabBg ?? "#EAF5FF"}`,
  flexWrap: "wrap",
  gap: "8px",
}));

export const StyledAttemptSubBlock = styled(Box, {
  shouldForwardProp: (prop) => prop !== "showDivider",
})(({ theme, showDivider }) => ({
  marginTop: showDivider ? "16px" : "14px",
  paddingTop: showDivider ? "16px" : 0,
  borderTop: showDivider
    ? `1px solid ${theme.palette?.custom?.tabBg ?? "#EAF5FF"}`
    : "none",
}));

export const StyledAttemptBadge = styled(Box)(({ color }) => ({
  padding: "3px 12px",
  borderRadius: "20px",
  backgroundColor: color || "#9c9c9c",
  color: "#fff",
  fontWeight: 700,
  fontSize: "0.65rem",
  textTransform: "uppercase",
  display: "inline-flex"
}));

export const StyledPaymentSectionBlock = styled(Box)(({ theme }) => ({
  backgroundColor: "#fafafa",
  border: theme.borders?.highlighted ?? "1px solid #CAE6FF",
  borderRadius: theme.shape?.custom?.regularRadius ?? "8px",
  padding: "10px 12px",
  marginBottom: "10px",
}));

export const StyledPaymentFieldGrid = styled(Box)(({ columns = 3 }) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
  gap: "10px",
  marginBottom: 0,
  "@media (max-width: 960px)": {
    gridTemplateColumns: columns > 2 ? "repeat(2, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))",
  },
  "@media (max-width: 600px)": {
    gridTemplateColumns: "1fr",
  },
}));

export const StyledPaymentFieldCell = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette?.custom?.white ?? "#FFFFFF",
  border: theme.borders?.boxBorder ?? "1px solid #D1D3D4",
  borderRadius: theme.shape?.custom?.regularRadius ?? "8px",
  padding: "8px 10px",
  minHeight: "auto",
  boxSizing: "border-box",
}));

export const StyledTextMonospace = styled(Typography)(({ theme }) => ({
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  fontSize: "0.9375rem",
  fontWeight: 700,
  lineHeight: 1.45,
  color: theme.palette?.custom?.OrderTextColor ?? PAYMENT_DETAIL_COLORS.fieldValue,
}));

export const StyledPaymentFieldLabel = styled(Typography)(({ theme }) => ({
  display: "block",
  marginBottom: "6px",
  fontSize: "0.8125rem",
  fontWeight: 600,
  lineHeight: 1.3,
  color: theme.palette?.custom?.blackColor ?? PAYMENT_DETAIL_COLORS.fieldLabel,
  letterSpacing: "0.01em",
}));

export const StyledPaymentFieldValue = styled(Typography)(({ theme }) => ({
  fontSize: "0.9375rem",
  fontWeight: 700,
  lineHeight: 1.45,
  color: theme.palette?.custom?.OrderTextColor ?? PAYMENT_DETAIL_COLORS.fieldValue,
  wordBreak: "break-word",
}));

export const StyledFailureReasonBox = styled(Box)(() => ({
  marginBottom: "16px",
  padding: "12px",
  borderRadius: "4px",
  backgroundColor: "#ffebee",
  border: "1px solid #ffcdd2"
}));

export const StyledTimelineContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  padding: "12px 14px",
  borderRadius: theme.shape?.custom?.regularRadius ?? "8px",
  backgroundColor: theme.palette?.custom?.white ?? "#FFFFFF",
  border: theme.borders?.boxBorder ?? "1px solid #D1D3D4",
  borderLeft: `4px solid ${theme.palette?.custom?.primary ?? PAYMENT_DETAIL_COLORS.accentBorder}`,
}));

export const StyledTimelineRow = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  fontSize: "0.8rem",
  paddingTop: "4px",
  paddingBottom: "4px",
}));

export const StyledHistoryItem = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px",
  borderRadius: "4px",
  border: "1px dashed #e0e0e0",
  fontSize: "0.8rem"
}));

export const StyledHistoryList = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  maxHeight: "150px",
  overflowY: "auto",
  paddingRight: "8px"
}));

// Added Components for complete style elimination
export const StyledProgressCentered = styled(Box)(() => ({
  display: "flex",
  justifyContent: "center",
  paddingTop: "24px",
  paddingBottom: "24px"
}));

export const StyledAdminInfoText = styled(Typography)(() => ({
  paddingTop: "16px",
  paddingBottom: "16px",
  fontSize: "0.875rem",
  color: "#666"
}));

export const StyledBoldHeaderLabel = styled(Typography)(({ theme }) => ({
  marginBottom: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  fontSize: "0.8125rem",
  fontWeight: 700,
  color: theme.palette?.custom?.primary ?? PAYMENT_DETAIL_COLORS.sectionTitle,
}));

export const StyledStatusText = styled(Typography)(({ textcolor }) => ({
  marginBottom: "8px",
  color: textcolor || "#FF8500"
}));

export const StyledAmountText = styled(Typography)(() => ({
  marginBottom: "16px",
  color: "#111"
}));

export const StyledFailureReasonSpan = styled(Box)(() => ({
  display: "inline",
  fontWeight: 600,
  color: "#111"
}));

export const StyledDividerMargin = styled(Divider)(() => ({
  marginBottom: "16px",
  marginTop: "16px"
}));

export const StyledHistoryStatus = styled(Typography)(({ textcolor }) => ({
  color: textcolor || "#9c9c9c",
  textTransform: "uppercase"
}));

export const StyledCircularProgress = styled(CircularProgress)(({ theme }) => ({
  color: theme?.palette?.custom?.primary || "#FF8500"
}));

export const StyledOrangeProgress = styled(CircularProgress)(() => ({
  color: "#FF8500"
}));

export const StyledGridContainer = styled(Grid)(() => ({
  marginBottom: "16px"
}));

export const StyledRetryButtonContainer = styled(Box)(() => ({
  paddingLeft: "40px"
}));

export const StyledTimelineLabel = styled(Typography)(() => ({
  marginBottom: "8px"
}));

export const StyledFailedButton = styled(StyledPayNowButton)(() => ({
  backgroundColor: "#FF3850",
  "&:hover": {
    backgroundColor: "#D32F2F"
  }
}));

export const StyledHeaderStatus = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "status"
})(({ status }) => {
  let color = "#FF8500"; // pending / in_progress
  if (status === "success" || status === "paid") color = "#00C046";
  if (status === "partial_paid") color = "#7B61FF";
  if (status === "failed") color = "#FF3850";
  if (status === "cancelled") color = "#455a64";
  return {
    color,
    fontSize: "0.75rem",
    fontWeight: 700,
    textTransform: "uppercase",
    border: `1px solid ${color}`,
    padding: "2px 8px",
    borderRadius: "4px",
    backgroundColor: `${color}0d`
  };
});

export const StyledPaymentSectionLabel = styled(Typography)(({ theme }) => ({
  marginTop: "0",
  marginBottom: "12px",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  fontSize: "0.8125rem",
  fontWeight: 700,
  color: theme.palette?.custom?.primary ?? PAYMENT_DETAIL_COLORS.sectionTitle,
}));

export const StyledManualProofSection = styled(Box)(() => ({
  marginTop: "0",
  marginBottom: "20px",
  paddingLeft: "2px",
  maxWidth: "360px",
}));

export const StyledProofImageWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  borderRadius: theme.borderRadius?.radius?.medium ?? "12px",
  overflow: "hidden",
  border: `1px solid ${theme.palette?.custom?.lightPink ?? "#e2e8f0"}`,
  backgroundColor: theme.palette?.custom?.screenBgColor ?? "#f8fafc",
  padding: "4px",
  display: "inline-block",
  boxSizing: "border-box"
}));

export const StyledProofImage = styled("img")(({ theme }) => ({
  width: "100%",
  maxHeight: "180px",
  objectFit: "contain",
  borderRadius: theme.borderRadius?.radius?.wallet ?? "8px",
  display: "block"
}));

export const StyledZoomButton = styled(IconButton)(() => ({
  position: "absolute",
  top: "10px",
  right: "10px",
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
  "&:hover": {
    backgroundColor: "#fff",
    transform: "scale(1.1)"
  },
  transition: "all 0.2s"
}));

export const StyledViewDocumentButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  borderRadius: theme.borderRadius?.radius?.small ?? "6px",
  color: theme.palette?.custom?.primary ?? "#0B65B5",
  borderColor: theme.palette?.custom?.lightPink ?? "#e2e8f0",
  padding: theme.padding?.horizontal?.medium ?? "8px 16px",
  width: "100%",
  display: "flex",
  justifyContent: "center",
  "&:hover": {
    borderColor: theme.palette?.custom?.primary ?? "#0B65B5",
    backgroundColor: theme.palette?.custom?.tabBg ?? "#f0f7ff"
  }
}));

export const StyledAdminInvoiceSection = styled(Box)(() => ({
  marginTop: "12px",
  marginBottom: "4px",
}));

export const StyledPackageNotesRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  marginTop: "8px",
}));

export const StyledPackageNotesLabel = styled(Typography)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  color: theme.palette.text.secondary,
}));

export const StyledPackageNotesButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  borderRadius: theme.borderRadius?.radius?.regular ?? "8px",
  borderColor: theme.palette.custom.orderCards,
  color: theme.palette.custom.orderCards,
  whiteSpace: "nowrap",
  "&:hover": {
    borderColor: theme.palette.custom.orderCards,
    backgroundColor: theme.palette.custom.chatBoxColor,
  },
}));

export const StyledPackageNotesPreview = styled(Typography)(({ theme }) => ({
  marginTop: "6px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: "72px",
  overflow: "hidden",
  display: "block",
  color: theme.palette.text.secondary,
}));

